import { Link } from "react-router-dom";
import { ArrowRight, Phone, Search, ClipboardList, Car, Check } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Reveal from "@/components/shared/Reveal";
import ProviderStrip from "@/components/shared/ProviderStrip";
import { BRAND, PROVIDERS } from "@/lib/data";

export default function About() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#0B2545] text-white pt-16 md:pt-24 pb-14 md:pb-20" data-testid="about-hero">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">About Carztohire</div>
          <h1 className="font-serif text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl">
            A US car rental aggregator built for one clear reason.
          </h1>
          <p className="mt-8 text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl">
            Renting a car in the US shouldn't require nine tabs and three phone calls. Carztohire
            compares every major provider in one place — so you can pick the best option in
            minutes, not hours.
          </p>
        </div>
      </section>

      <ProviderStrip />

      {/* Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">Our story</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight">
              Founded in Chattanooga, TN in 2019.
            </h2>
          </div>
          <div className="md:col-span-7 text-[#5B6472] text-lg leading-relaxed space-y-5 max-w-2xl">
            <p>
              Carztohire was founded by a small team of travel-industry veterans who were tired of
              the surprise fees and inconsistent pricing at rental counters.
            </p>
            <p>
              We built a simple comparison and inquiry service that surfaces options across every
              major US brand — Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, and
              Thrifty — and hands the reservation off to the provider you choose.
            </p>
            <p>
              We never charge a booking fee. We're paid by our provider partners only after a
              successful rental.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28 bg-[#F5F7FA] border-y border-line">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="mb-12 md:mb-16">
            <div className="overline mb-4">How it works</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0B2545] leading-tight max-w-3xl">
              Three steps, and we handle the comparison.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="card-flat p-8 h-full">
                  <div className="flex items-start justify-between mb-6">
                    <span className="font-mono text-xs text-[#5B6472]">{String(i+1).padStart(2,"0")}</span>
                    <div className="h-11 w-11 border border-[#0B2545] flex items-center justify-center text-[#0B2545]">
                      <s.icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#0B2545] leading-tight mb-3">{s.t}</h3>
                  <p className="text-[#5B6472] leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
          <div>
            <div className="overline mb-4">Partner network</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-6">
              Nine major national brands. Dozens of regional operators.
            </h2>
            <p className="text-[#5B6472] leading-relaxed max-w-lg">
              Every option surfaced through Carztohire is fulfilled by a licensed third-party
              rental provider. Contracts, insurance, and vehicles are theirs. Comparison and
              follow-up are ours.
            </p>
            <div className="mt-8 space-y-3">
              {["Every major US rental brand", "Regional operators in 10+ major cities", "Coverage in all 50 states", "No hidden markup — provider pricing"].map((p) => (
                <div key={p} className="flex items-center gap-3 text-[#0B2545]">
                  <Check className="h-5 w-5" strokeWidth={2} />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
            {PROVIDERS.map((p, i) => (
              <li key={p} className="flex items-baseline gap-4 border-b border-line pb-4">
                <span className="font-mono text-[10px] text-[#5B6472]">{String(i+1).padStart(2, "0")}</span>
                <span className="font-serif text-2xl md:text-3xl text-[#0B2545]">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#0B2545] text-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-white max-w-3xl">
            Call the team or request a free quote — we usually get back within an hour.
          </h2>
          <div className="mt-10 flex flex-col md:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-inverse" data-testid="about-call">
              <Phone className="h-4 w-4" strokeWidth={2} /> {BRAND.phone}
            </a>
            <Link to="/contact" className="btn-ghost-dark" data-testid="about-quote">
              Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

const STEPS = [
  { icon: Search, t: "You tell us the trip",   d: "Pickup city, dates, category, and lead-driver age. Fill it in once — takes 30 seconds." },
  { icon: ClipboardList, t: "We compare across every major brand", d: "Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, and Thrifty. Live availability, honest pricing." },
  { icon: Car, t: "You choose, we book",       d: "Pick the provider you prefer and we hand off the reservation. Your contract is directly with the rental company." },
];
